// Numbers, dates, and words. The spa is in Antioch, IL, so times show in Central time.

export const TZ = 'America/Chicago';

export function num(n: number): string {
  return n.toLocaleString('en-US');
}

export function plural(n: number, one: string, many = one + 's'): string {
  return `${num(n)} ${n === 1 ? one : many}`;
}

export function pct(part: number, whole: number): string {
  if (!whole) return '0%';
  const p = (part / whole) * 100;
  return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1)}%`;
}

function parts(iso: string, tz = TZ) {
  const d = new Date(iso);
  return {
    d,
    day: d.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' }),
    weekday: d.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' }),
    time: d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }),
    ymd: d.toLocaleDateString('en-CA', { timeZone: tz }),
  };
}

/** "today 4:30 PM", "yesterday", "Tue 9:00 AM" (this week), "Oct 3". */
export function when(iso: string | null | undefined, tz = TZ): string {
  if (!iso) return '';
  const p = parts(iso, tz);
  const today = parts(new Date().toISOString(), tz).ymd;
  const y = parts(new Date(Date.now() - 86_400_000).toISOString(), tz).ymd;
  const tm = parts(new Date(Date.now() + 86_400_000).toISOString(), tz).ymd;
  if (p.ymd === today) return `today ${p.time}`;
  if (p.ymd === y) return `yesterday ${p.time}`;
  if (p.ymd === tm) return `tomorrow ${p.time}`;
  const days = Math.abs(p.d.getTime() - Date.now()) / 86_400_000;
  if (days < 6) return `${p.weekday} ${p.time}`;
  return p.day;
}

/** "2 min ago", "3 hr ago", then a date. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 50) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} hr ago`;
  return when(iso);
}

export function clock(d: Date, tz = TZ): string {
  return d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
}

/** "done by about 4:30 PM" or "done by about Thu 11:00 AM". */
export function eta(minutes: number | null | undefined): string {
  if (minutes == null) return '';
  if (minutes <= 1) return 'almost done';
  const end = new Date(Date.now() + minutes * 60_000);
  const label = minutes < 18 * 60 ? clock(end) : when(end.toISOString());
  return `done by about ${label}`;
}

export function greeting(name = 'Jen'): string {
  const h = Number(new Date().toLocaleString('en-US', { timeZone: TZ, hour: 'numeric', hour12: false }));
  const part = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  return `${part}, ${name}`;
}

export function fullName(c: { first_name: string | null; last_name: string | null; email: string }): string {
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email;
}

export function initials(c: { first_name: string | null; last_name: string | null; email: string }): string {
  const a = (c.first_name || c.email)[0] || '?';
  const b = c.last_name?.[0] || '';
  return (a + b).toUpperCase();
}

/** How far `tz`'s clock is ahead of UTC at an instant, in ms (Chicago: -5 or -6 hours). Never uses the device's zone. */
function tzOffsetMs(ms: number, tz = TZ): number {
  const p: Record<string, string> = {};
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
  for (const x of f.formatToParts(new Date(ms))) p[x.type] = x.value;
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - Math.floor(ms / 1000) * 1000;
}

/**
 * A date (YYYY-MM-DD) + time (HH:MM) typed in Central time -> ISO string, on any device.
 * Null if the input is not a real date, or the time is skipped when clocks spring forward.
 * A time that happens twice (fall back) means the first one.
 */
export function centralToIso(date: string, time: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})/.exec(time);
  if (!d || !t || +t[1] > 23 || +t[2] > 59) return null;
  const wall = Date.UTC(+d[1], +d[2] - 1, +d[3], +t[1], +t[2]);
  if (new Date(wall).getUTCDate() !== +d[3]) return null;
  // The offset is one of the two in effect half a day either side. Keep the ones that map back to the typed time.
  const fits = [wall - tzOffsetMs(wall - 43_200_000), wall - tzOffsetMs(wall + 43_200_000)].filter((ms) => ms + tzOffsetMs(ms) === wall);
  return fits.length ? new Date(Math.min(...fits)).toISOString() : null;
}

export function centralYmd(d = new Date()): string {
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}
